import { useLanguage } from '../../../contexts/LanguageContext';
import MultipleChoice from '../exercises/MultipleChoice';
import FillInBlank from '../exercises/FillInBlank';
import MatchPairs from '../exercises/MatchPairs';
import DragDropSentence from '../exercises/DragDropSentence';
import FlashCard from '../exercises/FlashCard';
import TranslationExercise from '../exercises/TranslationExercise';
import IntroPhase from './IntroPhase';
import TeachingPhase from './TeachingPhase';
import StoryMode from '../exercises/StoryMode';
import SwipeVocabularyCard from '../exercises/SwipeVocabularyCard';
import VocabularyTimeRace from '../exercises/VocabularyTimeRace';
import WordHunt from '../exercises/WordHunt';

/**
 * Dispatches the correct exercise component based on phase.type.
 * Covers 6 main exercise types + intro + M5.5 advanced exercises.
 *
 * onPhaseComplete signature: ({ score, maxScore, answers, timeSpent })
 */
export default function PhaseDispatcher({
  phase,
  onPhaseComplete,
  checkAnswer,
  'data-testid': testId = 'phase-dispatcher',
}) {
  const { t } = useLanguage();

  if (!phase) return null;

  const { type } = phase;

  switch (type) {
    case 'intro':
      return (
        <IntroPhase
          phase={phase}
          onPhaseComplete={onPhaseComplete}
          data-testid={`${testId}-intro`}
        />
      );

    case 'teaching':
    case 'lesson':
    case 'reading':
      return (
        <TeachingPhase
          phase={phase}
          onPhaseComplete={onPhaseComplete}
          data-testid={`${testId}-teaching`}
        />
      );

    case 'multiple_choice':
      return (
        <MultipleChoice
          phase={phase}
          onPhaseComplete={onPhaseComplete}
          checkAnswer={checkAnswer}
          data-testid={`${testId}-mc`}
        />
      );

    case 'fill_in_blank':
      return (
        <FillInBlank
          phase={phase}
          onPhaseComplete={onPhaseComplete}
          checkAnswer={checkAnswer}
          data-testid={`${testId}-fib`}
        />
      );

    case 'matching':
    case 'match_pairs':
      return (
        <MatchPairs
          phase={phase}
          onPhaseComplete={onPhaseComplete}
          checkAnswer={checkAnswer}
          data-testid={`${testId}-match`}
        />
      );

    case 'drag_drop':
    case 'drag_drop_sentence':
      return (
        <DragDropSentence
          phase={phase}
          onPhaseComplete={onPhaseComplete}
          checkAnswer={checkAnswer}
          data-testid={`${testId}-drag`}
        />
      );

    case 'flashcard':
    case 'flash_card':
      return (
        <FlashCard
          phase={phase}
          onPhaseComplete={onPhaseComplete}
          data-testid={`${testId}-flash`}
        />
      );

    case 'translation':
    case 'translation_exercise':
      return (
        <TranslationExercise
          phase={phase}
          onPhaseComplete={onPhaseComplete}
          checkAnswer={checkAnswer}
          data-testid={`${testId}-translation`}
        />
      );

    // ── M5.5 Advanced exercises ───────────────────────────────────────
    case 'story_mode':
      return (
        <StoryMode
          phase={phase}
          onPhaseComplete={onPhaseComplete}
          data-testid={`${testId}-story`}
        />
      );

    case 'swipe':
    case 'swipe_vocabulary_card':
      return (
        <SwipeVocabularyCard
          phase={phase}
          onPhaseComplete={onPhaseComplete}
          checkAnswer={checkAnswer}
          data-testid={`${testId}-swipe`}
        />
      );

    case 'time_race':
    case 'vocabulary_time_race':
      return (
        <VocabularyTimeRace
          phase={phase}
          onPhaseComplete={onPhaseComplete}
          data-testid={`${testId}-race`}
        />
      );

    case 'word_hunt':
      return (
        <WordHunt
          phase={phase}
          onPhaseComplete={onPhaseComplete}
          data-testid={`${testId}-hunt`}
        />
      );

    // ── Still coming soon ─────────────────────────────────────────────
    case 'dialogue':
    case 'dialogue_simulation':
    case 'vocabulary':
    case 'pronunciation':
    case 'listening':
    case 'role_play':
    case 'crossword':
    case 'word_scramble':
    case 'memory_trick':
    case 'simple_speaking':
    case 'speaking_practice':
    case 'visual_cards':
    case 'video_lesson':
      return (
        <ComingSoonPhase
          type={type}
          onPhaseComplete={onPhaseComplete}
          testId={testId}
          t={t}
        />
      );

    default:
      return (
        <ComingSoonPhase
          type={type}
          onPhaseComplete={onPhaseComplete}
          testId={testId}
          t={t}
        />
      );
  }
}

function ComingSoonPhase({ type, onPhaseComplete, testId, t }) {
  return (
    <div
      className="flex flex-col items-center justify-center gap-4 py-12 text-center"
      data-testid={`${testId}-coming-soon`}
    >
      <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-2xl text-3xl" aria-hidden="true">
        🚧
      </div>
      <div>
        <p className="text-lg font-bold text-[var(--text-main)] mb-1">
          {t('lesson.coming_soon_title') || 'Yakında Geliyor'}
        </p>
        <p className="text-sm text-[var(--text-muted)]">
          {t('lesson.coming_soon_desc') || `"${type}" egzersiz tipi M5.5 güncellemesinde eklenecek.`}
        </p>
      </div>
      <button
        onClick={() => onPhaseComplete({ score: 0, maxScore: 0, answers: {}, timeSpent: 0 })}
        className="px-6 py-2.5 bg-[var(--brand-primary)] text-white font-bold rounded-xl hover:bg-[var(--brand-primary-hover)] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
        data-testid={`${testId}-coming-soon-skip`}
      >
        {t('lesson.skip_phase') || 'Atla ve Devam Et'}
      </button>
    </div>
  );
}
